import { c as _c } from "react/compiler-runtime";
import { Button, Tooltip, IconButton, Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { Condition, Provider } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { useDAL } from '@/contexts/useDAL';
import { isValueCondition } from '@/types/advanceFilter';
import { Loader } from '../../layout/loading';
import { SearchTextBox } from '../../search/searchTextBox';
import { MultiSelectFilter, type FilterProps } from '../filters';
export interface TagsFilterProps extends FilterProps {
  supportedProviders?: Provider[];
  onDoneClick: () => void;
  initialFilterMode: 'key' | 'value' | 'key-value';
  hideButton?: boolean;
  popoverOpen?: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
  dataSource?: 'tags' | 'sourceAccountTags';
}
export interface TagsDataResult {
  data: {
    tags?: {
      key: string;
      value?: string | null;
    }[];
  } | undefined;
  isLoading: boolean;
}
export function useResourceTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.tags.useList(filter);
}
export function useSourceAccountTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.sourceAccountTags.useList(filter);
}
const ResourceTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useResourceTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useSourceAccountTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsFilter = props => {
  const $ = _c(4);
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <SourceAccountTagsFilterWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceTagsFilterWrapper {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const TagsFilterContent = props => {
  const $ = _c(66);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const [filterMode, setFilterMode] = useState(props.initialFilterMode);
  const [isScrolledVertically, setScrolledVertically] = useState(false);
  const {
    data: body,
    isLoading
  } = props.tagsData;
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property) {
    let t1;
    if ($[3] !== props.property) {
      t1 = x => isValueCondition(x) && x.property === props.property.name;
      $[3] = props.property;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = props.conditions.find(t1);
    $[0] = props.conditions;
    $[1] = props.property;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[5] !== condition) {
    t1 = isValueCondition(condition) && condition?.value || [];
    $[5] = condition;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const selectedResources = t1;
  let t2;
  if ($[7] !== body?.tags) {
    t2 = body?.tags || [];
    $[7] = body?.tags;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const tags = t2;
  selectedResources.forEach(tag => {
    const [key, value] = tag.split("=");
    if (!tags.some(t => t.key === key && t.value === value)) {
      tags.push({
        key,
        value
      });
    }
  });
  const allTags = tags;
  let t3;
  if ($[9] !== allTags) {
    t3 = allTags.reduce(_temp, {
      keys: new Set(),
      values: new Set()
    });
    $[9] = allTags;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const tagsReduced = t3;
  let t4;
  if ($[11] !== keyFilter || $[12] !== tagsReduced.keys) {
    t4 = Array.from(tagsReduced.keys).filter(key_0 => !keyFilter || key_0.toLowerCase().includes(keyFilter.toLowerCase())).map(_temp2);
    $[11] = keyFilter;
    $[12] = tagsReduced.keys;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const keys = t4;
  let t5;
  if ($[14] !== tagsReduced.values || $[15] !== valueFilter) {
    t5 = Array.from(tagsReduced.values).filter(value_0 => !valueFilter || value_0.toLowerCase().includes(valueFilter.toLowerCase())).sort().map(_temp3);
    $[14] = tagsReduced.values;
    $[15] = valueFilter;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const values = t5;
  let t6;
  if ($[17] !== allTags || $[18] !== keyFilter || $[19] !== valueFilter) {
    let t7;
    if ($[21] !== keyFilter || $[22] !== valueFilter) {
      t7 = t_0 => (!keyFilter || t_0.key.toLowerCase().includes(keyFilter.toLowerCase())) && (!valueFilter || !!t_0.value?.toLowerCase().includes(valueFilter.toLowerCase()));
      $[21] = keyFilter;
      $[22] = valueFilter;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    const allValues = Array.from(new Set(allTags.filter(t7).map(_temp4)));
    t6 = allValues.map(_temp5);
    $[17] = allTags;
    $[18] = keyFilter;
    $[19] = valueFilter;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const keyValuePairs = t6;
  if (isLoading) {
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Loader />;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    return t7;
  }
  const options = filterMode === "key" ? keys : filterMode === "value" ? values : keyValuePairs;
  const t7 = isScrolledVertically ? "var(--mui-customShadows-panel)" : undefined;
  let t8;
  if ($[25] !== t7) {
    t8 = {
      borderBottom: "1px solid var(--mui-palette-divider)",
      boxShadow: t7
    };
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Typography>Filter by</Typography>;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  let t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event => setFilterMode(event.target.value);
    t11 = <MenuItem value="key">Tag keys</MenuItem>;
    t12 = <MenuItem value="key-value">Key-value pairs</MenuItem>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
  }
  let t13;
  if ($[31] !== filterMode) {
    t13 = <Select value={filterMode} onChange={t10}>{t11}{t12}</Select>;
    $[31] = filterMode;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = `To filter your resources, select one or more ${filterMode}${filterMode === "key" || filterMode === "value" ? "s" : " pairs"}.`;
  let t15;
  if ($[33] !== t14) {
    t15 = <Typography className="my-[12px]" variant="body1">{t14}</Typography>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== filterMode || $[36] !== keyFilter || $[37] !== valueFilter) {
    t16 = (filterMode.includes("key") || filterMode.includes("value")) && <Stack direction="row" alignItems="center" gap="12px" className="w-full"><Stack gap="12px" flexGrow={1}>{filterMode.includes("key") && <SearchTextBox value={keyFilter} onChange={setKeyFilter} placeholder="Search by key" />}{filterMode.includes("value") && <SearchTextBox value={valueFilter} onChange={setValueFilter} placeholder="Search by value" />}</Stack></Stack>;
    $[35] = filterMode;
    $[36] = keyFilter;
    $[37] = valueFilter;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t13 || $[40] !== t15 || $[41] !== t16 || $[42] !== t8) {
    t17 = <Stack gap="12px" className="px-[24px] pb-[24px] py-[12px]" sx={t8}>{t9}{t13}{t15}{t16}</Stack>;
    $[39] = t13;
    $[40] = t15;
    $[41] = t16;
    $[42] = t8;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== isScrolledVertically) {
    t18 = event_0 => {
      const target = event_0.target as HTMLDivElement;
      if (typeof target.scrollTop === "number") {
        const isScrolled = target.scrollTop > 0;
        if (isScrolled !== isScrolledVertically) {
          setScrolledVertically(isScrolled);
        }
      }
    };
    $[44] = isScrolledVertically;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== options.length) {
    t19 = options.length === 0 && <Typography variant="body1" className="my-[12px]">{"No matching values found. Try a new search term."}</Typography>;
    $[46] = options.length;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== keyFilter || $[49] !== options || $[50] !== props || $[51] !== valueFilter) {
    t20 = options.length > 0 && <MultiSelectFilter {...props} showSelectAll={!!keyFilter || !!valueFilter} options={options} />;
    $[48] = keyFilter;
    $[49] = options;
    $[50] = props;
    $[51] = valueFilter;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t18 || $[54] !== t19 || $[55] !== t20) {
    t21 = <Box flexGrow={1} className="overflow-auto px-[24px] py-[12px]" onScroll={t18}>{t19}{t20}</Box>;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== condition || $[58] !== filterMode || $[59] !== props || $[60] !== selectedResources.length) {
    t22 = selectedResources.length > 0 && <Stack direction="row" alignItems="center" className="px-[24px] py-[16px]" justifyContent="space-between" sx={{
      boxShadow: "var(--mui-customShadows-panelUp)"
    }}><Typography>{`${selectedResources.length} ${filterMode === "key" || filterMode === "value" ? filterMode : "pair"}${selectedResources.length === 1 ? "" : "s"} selected`}</Typography><Stack direction="row" alignItems="center"><Tooltip title="Clear all"><IconButton onClick={() => props.onChange(props.conditions.filter(x_1 => x_1 !== condition))}><i className="material-symbols-filter-alt-off-outline-rounded text-(--mui-palette-text-primary) h-[20px] w-[20px]" /></IconButton></Tooltip><Button className="ml-[12px]" variant="outlined" onClick={() => props.onDoneClick()}>Done</Button></Stack></Stack>;
    $[57] = condition;
    $[58] = filterMode;
    $[59] = props;
    $[60] = selectedResources.length;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t17 || $[63] !== t21 || $[64] !== t22) {
    t23 = <Stack className="h-full overflow-hidden">{t17}{t21}{t22}</Stack>;
    $[62] = t17;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  return t23;
};
function _temp(acc, tag_0) {
  acc.keys.add(tag_0.key);
  if (tag_0.value) {
    acc.values.add(tag_0.value);
  }
  return acc;
}
function _temp2(key_1) {
  return {
    id: key_1,
    name: key_1
  };
}
function _temp3(value_1) {
  return {
    id: value_1,
    name: value_1
  };
}
function _temp4(x_0) {
  return `${x_0.key}=${x_0.value || ""}`;
}
function _temp5(value_2) {
  return {
    id: value_2,
    name: value_2
  };
}